import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, orderBy, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { sessionStart, sessionLabel, resolveZone, zoneCity } from '../dateUtils';
import { startCall } from './VideoCall';
import { Icon } from './ui';
import { toast } from './feedback';
import { useBlocked, withoutBlocked } from '../blocking';
import { acceptSession, declineSession, offerCalendar, inLiveWindow, SESSION_MS } from '../sessionActions';

const MIN = 60 * 1000;

// Identical requests (same people, same direction, same time) show as one row.
export const groupSame = (list) => {
  const groups = new Map();
  list.forEach(s => {
    const key = [s.fromUid, s.toUid, sessionStart(s)?.getTime() ?? s.slot].join('|');
    if (groups.has(key)) groups.get(key).dupes.push(s);
    else groups.set(key, { ...s, dupes: [] });
  });
  return [...groups.values()];
};

function Sessions({ user, userProfile, onFindMatches }) {
  const zone = resolveZone(userProfile?.timezone);
  const [sent, setSent] = useState([]);
  const [received, setReceived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!user?.uid) return;
    const q1 = query(collection(db,'sessions'),where('fromUid','==',user.uid),orderBy('createdAt','desc'));
    const q2 = query(collection(db,'sessions'),where('toUid','==',user.uid),orderBy('createdAt','desc'));
    const u1 = onSnapshot(q1, snap => { setSent(snap.docs.map(d=>({id:d.id,...d.data(),isMine:true}))); setLoading(false); });
    const u2 = onSnapshot(q2, snap => { setReceived(snap.docs.map(d=>({id:d.id,...d.data(),isMine:false}))); });
    return () => { u1(); u2(); };
  }, [user?.uid]);

  // Re-check every minute, so Join video appears on time.
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), MIN); return () => clearInterval(t); }, []);

  const blocked = useBlocked(user?.uid);
  const myName = userProfile?.name || 'Your study partner';
  const partnerOf = s => s.isMine ? s.toName : s.fromName;
  const partnerUid = s => s.isMine ? s.toUid : s.fromUid;
  const startOf = s => sessionStart(s)?.getTime() ?? null;

  const all = withoutBlocked([...sent,...received], blocked, partnerUid)
    .filter(s => s.status === 'accepted' || s.status === 'pending')
    .sort((a, b) => (startOf(a) || 0) - (startOf(b) || 0));
  const isPast = s => { const t = startOf(s); return t != null && t + SESSION_MS < now; };
  const upcoming = all.filter(s => !isPast(s));
  const requests = groupSame(upcoming.filter(s => !s.isMine && s.status === 'pending'));
  const confirmed = upcoming.filter(s => s.status === 'accepted');
  const waiting = groupSame(upcoming.filter(s => s.isMine && s.status === 'pending'));
  const past = all.filter(isPast).reverse();

  const accept = async (r) => {
    const ok = await acceptSession(r, { uid: user.uid, myName, zone });
    // The copies of the same request are answered with it, quietly.
    if (ok) await Promise.all(r.dupes.map(d => updateDoc(doc(db,'sessions',d.id),{status:'declined'}).catch(() => {})));
  };

  const decline = async (r) => {
    await Promise.all(r.dupes.map(d => updateDoc(doc(db,'sessions',d.id),{status:'declined'}).catch(() => {})));
    await declineSession(r, { uid: user.uid, myName });
  };

  const withdraw = async (r) => {
    try {
      await Promise.all([r, ...r.dupes].map(d => updateDoc(doc(db,'sessions',d.id),{status:'withdrawn'})));
      toast(`Request to ${r.toName} withdrawn.`);
    } catch(e) { console.error(e); toast('Could not withdraw the request.', { type: 'error' }); }
  };

  const subjects = s => (s.subjects||[]).length > 0 && <div className="session-subjects">{s.subjects.map(sub=><span key={sub} className="chip">{sub}</span>)}</div>;

  const requestCard = r => (
    <div key={r.id} className="session-card request">
      <div className="session-date-time"><Icon name="calendar" size={20} />{sessionLabel(r, zone)}</div>
      <div className="session-partner"><strong>{r.fromName}</strong> asked to study with you</div>
      {subjects(r)}
      <div className="request-actions">
        <button className="btn-small btn-accept" onClick={()=>accept(r)}>Accept</button>
        <button className="btn-small btn-decline" onClick={()=>decline(r)}>Decline</button>
      </div>
    </div>
  );

  const confirmedCard = s => {
    const partner = partnerOf(s);
    const live = inLiveWindow(s, now);
    return (
      <div key={s.id} className="session-card">
        <span className="status-pill confirmed">{live ? 'Now' : 'Confirmed'}</span>
        <div className="session-date-time"><Icon name="calendar" size={20} />{sessionLabel(s, zone)}</div>
        <div className="session-partner">With: <strong>{partner}</strong></div>
        {subjects(s)}
        {live ? (
          <button className="btn session-join" onClick={()=>startCall(partnerUid(s), partner)}>
            <Icon name="video" size={20} />Join video
          </button>
        ) : (
          <div className="session-actions">
            <button className="btn-small secondary" onClick={()=>offerCalendar(s, zone)}>
              <Icon name="calendar" size={18} />Add to calendar
            </button>
            <span className="session-note">Video opens 10 min before</span>
          </div>
        )}
      </div>
    );
  };

  const waitingRow = r => (
    <div key={r.id} className="session-row">
      <div className="session-row-text">
        <strong>{r.toName}</strong>
        <span>{sessionLabel(r, zone)}</span>
      </div>
      <button className="btn-text" onClick={()=>withdraw(r)}>Withdraw</button>
    </div>
  );

  const pastCard = s => {
    const pending = s.status === 'pending';
    return (
      <div key={s.id} className="session-card past">
        <span className={`status-pill ${pending?'expired':'confirmed'}`}>{pending?'Expired':'Confirmed'}</span>
        <div className="session-date-time"><Icon name="calendar" size={20} />{sessionLabel(s, zone)}</div>
        <div className="session-partner">With: <strong>{partnerOf(s)}</strong></div>
        {subjects(s)}
      </div>
    );
  };

  return (
    <>
      <p className="page-sub">Times in your timezone ({zoneCity(resolveZone(userProfile?.timezone))})</p>
      {loading?<div className="empty-state">Loading...</div>
      :all.length===0?(
        <div className="card empty-state">
          <div className="empty-icon"><Icon name="calendar" size={40} /></div>
          <p className="small">No study sessions yet. Schedule one from a match card.</p>
          {onFindMatches && <button className="btn-small secondary" style={{marginTop:'16px'}} onClick={onFindMatches}>Find a partner</button>}
        </div>
      ):(
        <>
          {requests.length>0&&(
            <>
              <div className="section-label">Requests ({requests.length})</div>
              {requests.map(requestCard)}
            </>
          )}
          <div className="section-label">Confirmed ({confirmed.length})</div>
          {confirmed.length===0
            ? <div className="card empty-state compact"><p>No confirmed sessions coming up.</p></div>
            : confirmed.map(confirmedCard)}
          {waiting.length>0&&(
            <>
              <div className="section-label">Waiting for reply ({waiting.length})</div>
              <div className="card session-rows">{waiting.map(waitingRow)}</div>
            </>
          )}
          {past.length>0&&(
            <details className="past-sessions">
              <summary>Past sessions ({past.length})</summary>
              {past.map(pastCard)}
            </details>
          )}
        </>
      )}
    </>
  );
}
export default Sessions;
